import React, { useEffect } from 'react';
import {   InfoBox,   ExerciseBox,   CodeBlock, CodeEditor, BoldTextParagraph,   InlineCode,   SectionTitle,  Note,  TableContainer,  Highlight,  PatternContainer,  SectionHeading,  SubHeading,  TwoColGrid,  ThreeColGrid,  GridItem,  Divider,  MutedText, Separator, CorrectionBox, Result} from '../../../../components/course/CourseComponents';

const Chapitre5Content = ({ courseId = 'baseprogrammation' }) => {
  
  // Effet pour donner le focus à l'éditeur quand le composant se monte
  useEffect(() => {
    // Le focus sera géré par la prop autoFocus du CodeEditor
  }, []);

  return (
    <>
     <InfoBox title="Objectif" color="blue">
        <p className="mb-0">
        Répéter une action plusieurs fois automatiquement.
        </p>
      </InfoBox>       

      <Separator type="text" text="Qu'est-ce qu'une boucle ?" color="gray-darker" size="md" />
      <SectionTitle>Qu'est-ce qu'une boucle ?</SectionTitle>
      <p>
        Une boucle permet de faire plusieurs fois la même chose, sans copier-coller le code. 
        C'est très utile dans les jeux : pour animer des objets, compter des points, répéter une action tant qu'une condition est vraie, etc.
      </p>

      <p>Il existe deux types de boucles principales :</p>
      <ul className="list-disc list-inside mb-4">
        <li><InlineCode>for</InlineCode> → pour compter</li>
        <li><InlineCode>while</InlineCode> → tant que c'est vrai</li>
      </ul>

      <Separator type="text" text="La boucle for" color="gray-darker" size="md" />
      <SectionTitle>La boucle for</SectionTitle>
      <p>
        Elle est parfaite pour répéter un certain nombre de fois.
      </p>

      <Note type="gray" title="🔁 Exemple de boucle for :">
        <CodeBlock language="javascript">
{`for (let i = 0; i < 5; i++) {
  print("Salut !");
}`}
        </CodeBlock>
        <p  >👉 Affiche 5 fois "Salut !"</p>
      </Note>

      <Note type="info" title="💡 Explication :">
        <ul className="list-disc list-inside">
          <li><InlineCode>let i = 0</InlineCode> → on declare une variable i qui commence à 0. i est notre <strong>compteur</strong></li>
          <li><InlineCode>i &lt; 5</InlineCode> → tant que i est plus petit que 5, on continue</li>
          <li><InlineCode>i++</InlineCode> → on ajoute 1 à i à chaque tour. Equivalent à <InlineCode>i = i + 1</InlineCode></li>
        </ul>
      </Note>

      <Note type="tip" title="💡 Astuce : i++"><InlineCode>i++</InlineCode> est équivalent à <InlineCode>i = i + 1</InlineCode>. C'est un maniere plus rapide d'incrementer la vairiable <InlineCode>i</InlineCode> de 1
      <br/>
      Si ca te semble compliqué, tu peux utiliser <InlineCode>i = i + 1</InlineCode> à la place.

      </Note>

      <Note type="exercice" title="🧪 Exercice table de multiplication avec un for :">
        Affiche la table de multiplication du 2 de 0 à 10 :<br/>
        - Utilise une boucle <InlineCode>for</InlineCode><br/>
        - Va de 0 à 10 (inclus)<br/>
        - Affiche "2 * i = résultat"<br/>
        <Result>2*0 = 0 | 2*1 = 2 | 2*2 = 4 | 2*3 = 6 | ...</Result>
        
        <CorrectionBox>
          <CodeBlock language="javascript">
{`for (let i = 0; i <= 10; i++) {
  print("2 * " + i + " = " + (2 * i));
}`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <CodeEditor
        courseId={courseId}
        editorId="boucles-for"
        title="Exercice : Boucle for et table de multiplication"
        showConsole={true}
        height="250px"
        initialCode=""
        autoFocus={true}
      />

      <Separator type="text" text="La boucle while" color="gray-darker" size="md" />
      <SectionTitle>La boucle while</SectionTitle>
      <p>
        Elle sert à répéter tant qu'une condition est vraie.
      </p>

      <Note type="gray" title="🔁 Exemple de boucle while :">
        <CodeBlock language="javascript">
{`let i = 0;
while (i < 3) {
  print("Coucou");
  i++;
}`}
        </CodeBlock>
        <p  >👉 Affiche 3 fois "Coucou"</p>
      </Note>

      <Note type="warning" title="⚠️ Attention :">
        N'oublie pas d'incrémenter la variable (ici <InlineCode>i++</InlineCode>), sinon la boucle tournera à l'infini !<br/>
        C'est ce qu'on appelle une boucle infinie. Si ça t'arrive, redémarre ton navigateur en faisant F5, pour stopper l'execution du programme.
      </Note>

      <Note type="exercice" title="🧪 Exercice table de multiplication avec un while :">
        Affiche la table de multiplication du 3 avec une boucle <InlineCode>while</InlineCode> :<br/>
        - Utilise une boucle <InlineCode>while</InlineCode><br/>
        - Va de 0 à 10 (inclus)<br/>
        - N'oublie pas d'incrémenter i !<br/>
        <Result>3*0 = 0 | 3*1 = 3 | 3*2 = 6 | 3*3 = 9 | ...</Result>
        
        <CorrectionBox>
          <CodeBlock language="javascript">
{`let i = 0;
while (i <= 10) {
  print("3 * " + i + " = " + (3 * i));
  i++;
}`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <CodeEditor
        courseId={courseId}
        editorId="boucles-while"
        title="Exercice : Boucle while et table de multiplication"
        showConsole={true}
        height="300px"
        initialCode=""
      />

      <Separator type="text" text="Sortir d'une boucle avec break" color="gray-darker" size="md" />
      <SectionTitle>Sortir d'une boucle avec break</SectionTitle>
      <p>
        Tu peux arrêter une boucle avant la fin avec le mot <InlineCode>break</InlineCode>.
      </p>

      <Note type="gray" title="⛔ Exemple avec break :">
        <CodeBlock language="javascript">
{`for (let i = 0; i < 10; i++) {
  if (i == 5) break;
  print(i);
}`}
        </CodeBlock>
        <p  >👉 Affiche 0 à 4 puis s'arrête</p>
      </Note>

      

      <p>
        ✅ Bravo ! Tu sais maintenant faire des actions répétées, ce qui est super utile dans les jeux !
      </p>
      
      <BoldTextParagraph>
        Prochaine étape : les listes (ou tableaux) 📋
      </BoldTextParagraph>

  </>
  );
};

export default Chapitre5Content; 